/** mock-tenants.ts
 * Données client/site mock pour la Console.
 * Convention semi-réelle : si un host a des tags `client:X` et `site:Y`,
 * on les utilise. Sinon repli sur le mapping ci-dessous.
 * TODO(backend) : remplacer par un champ DB host.clientId + host.siteId
 */

export interface ClientInfo {
  client: string;
  site: string;
}

/** Champs d'un host servant à résoudre client/site. */
export interface HostClientFields {
  hostId?: string;
  tags?: string[];
  clientLabel?: string | null;
  siteLabel?: string | null;
}

/** Résout client/site d'un host :
 *  1. Champs réels `clientLabel` / `siteLabel` (colonnes DB host_modules)
 *  2. Tags `client:X` / `site:Y`
 *  3. Fallback "Sans client" / "—"
 */
export function resolveClientSite(host: HostClientFields): ClientInfo {
  if (host.clientLabel || host.siteLabel) {
    return { client: host.clientLabel || "Sans client", site: host.siteLabel || "—" };
  }
  if (host.tags) {
    const clientTag = host.tags.find((t) => t.startsWith("client:"));
    const siteTag = host.tags.find((t) => t.startsWith("site:"));
    if (clientTag || siteTag) {
      return {
        client: clientTag ? clientTag.slice("client:".length) : "—",
        site: siteTag ? siteTag.slice("site:".length) : "—",
      };
    }
  }
  return { client: "Sans client", site: "—" };
}

/** Tous les clients distincts extraits d'une liste de hosts */
export function buildClientTree(hosts: HostClientFields[]): Map<string, Map<string, string[]>> {
  const tree = new Map<string, Map<string, string[]>>();
  for (const h of hosts) {
    const { client, site } = resolveClientSite(h);
    if (!tree.has(client)) tree.set(client, new Map());
    const sites = tree.get(client)!;
    if (!sites.has(site)) sites.set(site, []);
    sites.get(site)!.push(h.hostId ?? "");
  }
  return tree;
}

// ─── Mock : demandes en attente ────────────────────────────────────────────
// TODO(backend) : endpoint GET /host/pending-approvals

export interface PendingRequest {
  hostId: string;
  hostname: string;
  client: string;
  site: string;
  requestedAt: string;
}

export const MOCK_PENDING: PendingRequest[] = [];

// ─── Mock : télémétrie plugins ─────────────────────────────────────────────
// TODO(backend) : agréger depuis plugin_jobs + plugin_job_events

export interface PluginTelemetry {
  id: string;
  name: string;
  status: "enabled" | "disabled" | "beta" | "staging";
  health: number | null;
  calls7d: number;
  errors7d: number;
  latencyP95ms: number | null;
  lastError: string | null;
  layers: string[];
}

export const MOCK_PLUGIN_TELEMETRY: PluginTelemetry[] = [
  {
    id: "netinfo",
    name: "Network Info",
    status: "enabled",
    health: 1.0,
    calls7d: 1240,
    errors7d: 0,
    latencyP95ms: 118,
    lastError: null,
    layers: ["agent", "panel", "mcp"],
  },
  {
    id: "ad-tools",
    name: "AD Tools",
    status: "enabled",
    health: 0.96,
    calls7d: 520,
    errors7d: 3,
    latencyP95ms: 212,
    lastError: "il y a 2j",
    layers: ["agent", "helper", "panel"],
  },
  {
    id: "services",
    name: "Service Control",
    status: "enabled",
    health: 1.0,
    calls7d: 2100,
    errors7d: 0,
    latencyP95ms: 380,
    lastError: null,
    layers: ["agent", "panel", "mcp"],
  },
  {
    id: "exchange",
    name: "Exchange Mailbox",
    status: "beta",
    health: 0.82,
    calls7d: 35,
    errors7d: 6,
    latencyP95ms: null,
    lastError: "timeout 14h",
    layers: ["helper", "panel"],
  },
  {
    id: "fs-explorer",
    name: "File Explorer Pro",
    status: "enabled",
    health: 0.99,
    calls7d: 3450,
    errors7d: 0,
    latencyP95ms: 195,
    lastError: null,
    layers: ["agent", "panel"],
  },
  {
    id: "pkg-mgr",
    name: "Package Manager",
    status: "enabled",
    health: 0.98,
    calls7d: 1820,
    errors7d: 1,
    latencyP95ms: 410,
    lastError: "kb 14h",
    layers: ["agent", "panel", "mcp"],
  },
  {
    id: "bitwarden",
    name: "Bitwarden Bridge",
    status: "enabled",
    health: 1.0,
    calls7d: 412,
    errors7d: 0,
    latencyP95ms: 144,
    lastError: null,
    layers: ["agent", "panel", "mcp"],
  },
  {
    id: "wmi-probe",
    name: "WMI Probe",
    status: "staging",
    health: 0.7,
    calls7d: 14,
    errors7d: 4,
    latencyP95ms: null,
    lastError: "access denied",
    layers: ["agent", "helper"],
  },
  {
    id: "screenrec",
    name: "Screen Record+",
    status: "disabled",
    health: null,
    calls7d: 0,
    errors7d: 0,
    latencyP95ms: null,
    lastError: null,
    layers: ["agent"],
  },
];

export const MOCK_KPI = {
  calls7d: MOCK_PLUGIN_TELEMETRY.reduce((s, p) => s + p.calls7d, 0),
  errors7d: MOCK_PLUGIN_TELEMETRY.reduce((s, p) => s + p.errors7d, 0),
  pluginsAlert: MOCK_PLUGIN_TELEMETRY.filter((p) => p.health !== null && p.health < 0.9).length,
  latencyP95ms: 318,
};

export const MOCK_INVOC_STREAM = [
  "[15:42:18] netinfo.netinfo_adapters      host=WIN-A12  op=sessaddam  ✓ 118ms",
  "[15:42:14] services.svc_restart          host=WIN-A12  op=sessaddam  ✓ 412ms",
  "[15:41:52] ad-tools.ad_unlock_account    host=WIN-SRV  op=sessaddam  ✓ 220ms",
  "[15:39:01] exchange.mailbox_export       host=WIN-EX01 op=jblouin    ✗ TIMEOUT",
  "[15:38:44] fs-explorer.fs_download       host=MAC-DEV  op=jblouin    ✓ 1.8s",
  "[15:38:21] wmi-probe.wmi_query           host=LIN-BUILD op=sessaddam ✗ access denied",
];
